import { useRef, useState } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Download,
  MessageCircle,
  Copy,
  Facebook,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatTurkishDate } from "@/lib/api";

// Generic Share Dialog — takes a `renderCard` render prop that renders the
// visual card (SocialMediaCard or calendar card, etc.) and a `buildText`
// function that produces the WhatsApp/Copy text.
const ShareDialog = ({
  open,
  onOpenChange,
  title = "Paylaş",
  subtitle = "Aşağıdaki içeriği indirebilir veya paylaşabilirsiniz.",
  fileNamePrefix = "sahder",
  buildText,
  children,
  captureRef, // parent-provided ref (if children pass their own)
  whatsappImageOnly = false, // when true, only image URL is sent (no caption text)
}) => {
  const innerRef = useRef(null);
  const cardRef = captureRef || innerRef;
  const [busy, setBusy] = useState(null); // 'png' | 'wa' | 'copy' | null

  const buildPng = async () => {
    if (!cardRef.current) return null;

    // 1) Ensure custom fonts (Playfair Display / Manrope) are loaded before
    //    capture — otherwise html2canvas rasterises fallback fonts.
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* ignore */ }
    }

    // 2) Any ancestor with `transform` (e.g. AutoFitPreview scaling) makes
    //    html2canvas measure the target at a reduced size → blurry output.
    //    Temporarily neutralise ancestor transforms, capture, then restore.
    const restore = [];
    let el = cardRef.current.parentElement;
    while (el && el !== document.body) {
      const cs = window.getComputedStyle(el);
      if (cs.transform && cs.transform !== "none") {
        restore.push({ el, prev: el.style.transform });
        el.style.transform = "none";
      }
      el = el.parentElement;
    }

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: Math.max(2, window.devicePixelRatio || 2) * 1.5, // ~3x DPI for crisp text
        backgroundColor: "#F9F6F0",
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 15000,
        windowWidth: document.documentElement.offsetWidth,
        windowHeight: document.documentElement.offsetHeight,
      });
      return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1.0));
    } finally {
      restore.forEach(({ el, prev }) => { el.style.transform = prev; });
    }
  };

  const download = async () => {
    setBusy("png");
    try {
      const blob = await buildPng();
      if (!blob) throw new Error();
      const link = document.createElement("a");
      link.download = `${fileNamePrefix}-${Date.now()}.png`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Görsel indirildi");
    } catch (e) {
      toast.error("Görsel oluşturulamadı");
    } finally {
      setBusy(null);
    }
  };

  // Detect iOS (iPhone/iPad) — Web Share API for files is unreliable there;
  // use wa.me Universal Link with an uploaded image URL instead.
  const isIOS = () => {
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) ||
      (ua.includes("Mac") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
  };

  const shareViaNative = async (file) => {
    // Try Web Share API — but SKIP on iOS (contact picker doesn't open reliably
    // for image files in WhatsApp iOS share extension)
    if (isIOS()) return false;
    if (file && typeof navigator.share === "function") {
      try {
        await navigator.share({ files: [file] });
        return true;
      } catch (err) {
        if (err && err.name === "AbortError") return true;
      }
    }
    return false;
  };

  // Upload a Blob to backend and return the absolute public URL
  const uploadBlob = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `${fileNamePrefix}.png`, { type: "image/png" }));
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rel = data?.url || "";
      if (!rel) return "";
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      return rel.startsWith("http") ? rel : `${backend}${rel}`;
    } catch {
      return "";
    }
  };

  // Navigate to a URL preserving user activation (important for iOS
  // Universal Links → WhatsApp app). If a pre-opened window is provided,
  // set its location; otherwise navigate the top-level page directly.
  const openExternal = (url, preopened) => {
    if (preopened && !preopened.closed) {
      try {
        preopened.location.href = url;
        return;
      } catch {
        /* fallthrough */
      }
    }
    // Top-level navigation triggers iOS Universal Link handling reliably
    window.location.href = url;
  };

  const whatsApp = async () => {
    setBusy("wa");
    // Pre-open a blank window SYNCHRONOUSLY in the click handler.
    // Required on iOS Safari: any async work loses the user-activation
    // token, causing window.open (and Universal Links) to be blocked.
    const preopened = window.open("about:blank", "_blank");
    try {
      const blob = await buildPng();
      let publicUrl = "";
      if (blob) publicUrl = await uploadBlob(blob);

      // Build message: image URL only (or URL + caption if allowed)
      const caption = whatsappImageOnly ? "" : (buildText ? buildText() : "");
      const parts = [];
      if (publicUrl) parts.push(publicUrl);
      if (caption) parts.push(caption);
      const text = parts.join("\n\n");
      const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;

      openExternal(waUrl, preopened);

      if (publicUrl) {
        toast.success("WhatsApp açıldı — kişi seçin, görsel bağlantısı hazır.");
      } else {
        toast.info("WhatsApp açıldı — 'PNG İndir' ile görseli ekleyin.");
      }
    } catch (e) {
      if (preopened && !preopened.closed) preopened.close();
      toast.error("Paylaşım başlatılamadı");
    } finally {
      setBusy(null);
    }
  };

  const instagramShare = async () => {
    setBusy("ig");
    // Pre-open synchronously for iOS activation
    const preopened = window.open("about:blank", "_blank");
    try {
      const blob = await buildPng();
      const file = blob
        ? new File([blob], `${fileNamePrefix}.png`, { type: "image/png" })
        : null;

      // Try native share on non-iOS (Android + desktop with WebShare)
      if (await shareViaNative(file)) {
        if (preopened && !preopened.closed) preopened.close();
        toast.success("Paylaşıldı");
        return;
      }

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
      const target = isMobile ? "instagram://library" : "https://www.instagram.com/";
      openExternal(target, preopened);
      toast.info("Instagram açıldı — 'PNG İndir' ile görseli indirip Story/Post olarak yükleyin.");
    } catch (e) {
      if (preopened && !preopened.closed) preopened.close();
      toast.error("Paylaşım başlatılamadı");
    } finally {
      setBusy(null);
    }
  };

  const facebookShare = () => {
    const text = buildText ? buildText() : "";
    // Facebook Share requires a URL, we use the site URL as anchor
    const url = window.location.href;
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    window.open(fb, "_blank", "noopener,noreferrer,width=600,height=520");
  };

  const twitterShare = () => {
    const text = buildText ? buildText() : "";
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=520");
  };

  const copyText = async () => {
    setBusy("copy");
    try {
      await navigator.clipboard.writeText(buildText ? buildText() : "");
      toast.success("Metin panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1F2937]">
            {title}
          </DialogTitle>
          <p className="text-sm text-[#4B5563]">{subtitle}</p>
        </DialogHeader>

        <div className="py-4">
          {typeof children === "function" ? children(cardRef) : children}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 pt-2">
          <Button
            onClick={whatsApp}
            disabled={busy}
            data-testid="share-whatsapp-btn"
            className="bg-[#25D366] hover:bg-[#1ea955] text-white gap-2 h-11 col-span-2 sm:col-span-1"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </Button>
          <Button
            onClick={instagramShare}
            disabled={busy}
            data-testid="share-instagram-btn"
            className="text-white gap-2 h-11 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90"
          >
            <i className="fa-brands fa-instagram" aria-hidden="true" />
            Instagram
          </Button>
          <Button
            onClick={facebookShare}
            disabled={busy}
            data-testid="share-facebook-btn"
            variant="outline"
            className="border-[#1877F2]/40 text-[#1877F2] hover:bg-[#1877F2]/5 gap-2 h-11"
          >
            <Facebook className="w-4 h-4" />
            Facebook
          </Button>
          <Button
            onClick={twitterShare}
            disabled={busy}
            data-testid="share-twitter-btn"
            variant="outline"
            className="border-stone-300 text-[#1F2937] hover:bg-stone-100 gap-2 h-11"
          >
            <X className="w-4 h-4" />
            X (Twitter)
          </Button>
          <Button
            onClick={download}
            disabled={busy}
            data-testid="share-download-btn"
            variant="outline"
            className="border-stone-300 text-[#1F2937] hover:bg-stone-100 gap-2 h-11"
          >
            <Download className="w-4 h-4" />
            PNG İndir
          </Button>
          <Button
            onClick={copyText}
            disabled={busy}
            data-testid="share-copy-btn"
            variant="outline"
            className="border-stone-300 text-[#1F2937] hover:bg-stone-100 gap-2 h-11"
          >
            <Copy className="w-4 h-4" />
            Metni Kopyala
          </Button>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            data-testid="share-close-btn"
            className="text-[#4B5563]"
          >
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper: build WhatsApp-style text for an event
export const buildEventShareText = (ev, categoryMap = {}) => {
  if (!ev) return "";
  const cat = categoryMap[ev.category]?.label || ev.category;
  const when = formatTurkishDate(ev.event_datetime);
  return (
    `${cat} Daveti\n\n` +
    `${ev.whose_event}\n` +
    `${ev.families}\n\n` +
    `${when}\n` +
    `${ev.venue}\n${ev.address}\n\n` +
    `— ŞAHDER`
  );
};

export default ShareDialog;
